import { isMobile } from '@/core/helpers/ui.helper';
import { onBeforeUnmount, watch, type Ref } from 'vue';

const DISMISS_THRESHOLD_PX = 100;
const RESISTANCE = 0.6;

export function useDrawerGesture(
  drawerRef: Ref<HTMLElement | null>,
  onDismiss: () => void,
  options?: { enabled?: Ref<boolean> },
) {
  let startY = 0;
  let currentDeltaY = 0;
  let isDragging = false;
  let scrollableAtStart: HTMLElement | null = null;

  function isEnabled() {
    if (options?.enabled != null) return options.enabled.value;
    return !!isMobile.value;
  }

  function findScrollableParent(el: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = el;
    while (node && node !== drawerRef.value) {
      if (node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function onTouchStart(e: TouchEvent) {
    if (!isEnabled()) return;
    scrollableAtStart = findScrollableParent(e.target as HTMLElement);
    startY = e.touches[0].clientY;
    currentDeltaY = 0;
    isDragging = false;
  }

  function onTouchMove(e: TouchEvent) {
    if (!isEnabled() || startY === 0) return;
    const delta = e.touches[0].clientY - startY;

    if (scrollableAtStart) {
      const atTop = scrollableAtStart.scrollTop <= 0;
      if (!atTop || delta <= 0) {
        currentDeltaY = 0;
        applyTransform(0, 1);
        return;
      }
    }

    if (delta <= 0) {
      currentDeltaY = 0;
      applyTransform(0, 1);
      return;
    }

    isDragging = true;
    currentDeltaY = delta * RESISTANCE;
    const progress = Math.min(currentDeltaY / (DISMISS_THRESHOLD_PX * 2), 1);
    applyTransform(currentDeltaY, 1 - progress * 0.3);
  }

  function onTouchEnd() {
    if (!isDragging) {
      startY = 0;
      return;
    }

    if (currentDeltaY >= DISMISS_THRESHOLD_PX) {
      const el = drawerRef.value;
      if (el) {
        el.style.transition = 'transform 200ms ease, opacity 200ms ease';
        el.style.transform = 'translateY(100%)';
        el.style.opacity = '0';
      }
      setTimeout(() => {
        onDismiss();
      }, 200);
    } else {
      const el = drawerRef.value;
      if (el) {
        el.style.transition = 'transform 200ms ease, opacity 200ms ease';
        el.style.transform = '';
        el.style.opacity = '';
        setTimeout(() => {
          if (el) el.style.transition = '';
        }, 200);
      }
    }

    startY = 0;
    currentDeltaY = 0;
    isDragging = false;
  }

  function applyTransform(y: number, opacity: number) {
    const el = drawerRef.value;
    if (!el) return;
    el.style.transition = '';
    el.style.transform = y > 0 ? `translateY(${y}px)` : '';
    el.style.opacity = opacity < 1 ? String(opacity) : '';
  }

  function attach(el: HTMLElement) {
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
  }

  function detach(el: HTMLElement) {
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchmove', onTouchMove);
    el.removeEventListener('touchend', onTouchEnd);
  }

  watch(
    drawerRef,
    (newEl, oldEl) => {
      if (oldEl) detach(oldEl);
      if (newEl) attach(newEl);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    if (drawerRef.value) detach(drawerRef.value);
  });
}
